import { fabric } from 'fabric';
import { fromUint8Array } from 'js-base64';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, defer, EMPTY, merge, Observable, Subject } from 'rxjs';
import {
  filter,
  finalize,
  map,
  mergeMap,
  mergeWith,
  share,
  shareReplay,
  startWith,
  timestamp,
} from 'rxjs/operators';
import { MovedBoardsPlacement } from 'src/app/common/interfaces/sync-service-interface';
import { ScribbleType } from 'src/app/services/session-shared-data.service';
import { PendingStructsManager } from 'src/app/utilities/PendingStructsManager';
import { v4 as uuidv4 } from 'uuid';
import * as Y from 'yjs';
import {
  TemporaryUserMetadataEntryBase,
  TemporaryUserMetadataEntryYJS,
} from 'src/app/state/temporary-user-metadata-repository.service';
import { AiContentProcessor } from 'src/app/genai/AiContentProcessor';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { BoardItemsEncoderDecoder } from 'src/app/services/board-items-encoder-decoder.service';
import { BoardFolder, Frame, FrameItem, FramePrivacy, FrameType, Room } from '../../models/session';
import { SessionUpdate } from '../../models/session-sync';
import { ISpaceYjs } from '../../state/space.repository';
import { BoardItem } from '../session/items-canvas/board-items/board.item';
import { BoardWebViewer } from '../session/items-canvas/board-items/board-web-viewer';
import { ItemModel } from '../../models/itemModel';
import { BoardImage } from '../session/items-canvas/board-items/board-image';
import { BoardRemoteVideo } from '../session/items-canvas/board-items/board-remote-video';
import { ResourceItemModel } from '../../models/resource';
import { BoardLine } from '../session/items-canvas/board-items/board-line';
import { BoardStickyNote } from '../session/items-canvas/board-items/board-sticky-note';
import { BoardFormula } from '../session/items-canvas/board-items/board-formula';
import { BoardImagesPdf } from '../session/items-canvas/board-items/board-images-pdf';
import { FabricObject } from '../session/items-canvas/board-items/board-items-manager';
import { itemIsScribbleOrHighlight } from '../session/wb-canvas/fabric-utils';
import { BoardVideo } from '../session/items-canvas/board-items/board-video';
import { Manager } from './session-data-structures';

export enum UndoEnum {
  UNDO,
  REDO,
}

export interface YObject extends fabric.Object {
  // The user that created the object
  userId?: string;

  // The current version of the object
  // This is used to find the diff for two arrays of fabric elements
  vuid?: string;

  // Unique identifier for the object
  uid?: string;

  // Controls the state of this object, it takes 3 values
  // true | false | undefined
  // to prevent cyclic adding of objects we only process objects
  // whose fresh value is initially undefined
  fresh?: boolean;
  ScribbleType: ScribbleType;
}

export interface YCanvasItemObject extends FrameItem {
  // The user that created the object
  userId?: string;

  // The current version of the object
  // This is used to find the diff for two arrays of fabric elements
  vuid?: string;

  // Unique identifier for the object
  uid?: string;

  // Controls the state of this object, it takes 3 values
  // true | false | undefined
  // to prevent cyclic adding of objects we only process objects
  // whose fresh value is initially undefined
  fresh?: boolean;
}

// This does not guarantee that this canvas item is going to be rendered correctly, this should only be used to guarantee that the object is not a plain fabric object
export const _isYCanvasItem = (
  yObjectOrYCanvasObject: YObject | YCanvasItemObject,
): yObjectOrYCanvasObject is YCanvasItemObject =>
  Object.prototype.hasOwnProperty.call(yObjectOrYCanvasObject, 'model') ||
  Object.prototype.hasOwnProperty.call(yObjectOrYCanvasObject, 'position');

export const isValidYCanvasItem = (
  yObjectOrYCanvasObject: YObject | YCanvasItemObject,
): yObjectOrYCanvasObject is YCanvasItemObject => {
  const frameItem = yObjectOrYCanvasObject as FrameItem;
  const canvasItem = yObjectOrYCanvasObject as YCanvasItemObject;

  // Mario
  if (frameItem?.model === ItemModel.Mario) {
    const marioState = frameItem.mario_state?.resource;
    return Boolean(
      marioState &&
        // DEV_URL has `url`; DEPLOYED has `name`+`major_version`; GENERATED has `appId`.
        (marioState.url || (marioState.name && marioState.major_version) || marioState.appId),
    );
  }

  return Boolean(
    // `model` is defined and has one of the below value with the corresponding state object
    frameItem.model &&
      // Adding a check for position as this is a common pattern in corrupted objects
      frameItem.position &&
      // Image, video or document
      ((frameItem.model === ItemModel.Resource &&
        (canvasItem.resource_state?.resource_item_model === ResourceItemModel.IMAGE ||
          canvasItem.resource_state?.resource_item_model === ResourceItemModel.VIDEO ||
          canvasItem.resource_state?.resource_item_model === ResourceItemModel.DOCUMENT ||
          canvasItem.resource_state?.resource_item_model === ResourceItemModel.PDF_IMAGES ||
          canvasItem.resource_state?.resource_item_model === ResourceItemModel.PDF)) ||
        // Web viewer
        (frameItem.model === ItemModel.WebViewer && canvasItem.browser_state) ||
        // Formula
        (frameItem.model === ItemModel.Formula && canvasItem.equation_state) ||
        // iframe
        (frameItem.model === ItemModel.IFrame && canvasItem.iframe_state) ||
        // Question
        (frameItem.model === ItemModel.Question && canvasItem.question_state) ||
        // Note
        (frameItem.model === ItemModel.Note && canvasItem.question_state) ||
        // Worksheet
        (frameItem.model === ItemModel.Worksheet && canvasItem.worksheet_state) ||
        // ChatGPT
        (frameItem.model === ItemModel.Chat_GPT && canvasItem.gpt_state) ||
        // Remote video
        frameItem.model === ItemModel.RemoteVideo),
  );
};

export const isYObject = (
  yObjectOrYCanvasObject: YObject | YCanvasItemObject,
): yObjectOrYCanvasObject is YObject => !_isYCanvasItem(yObjectOrYCanvasObject);

export const isBoardItem = (yObjectOrYCanvasObject: any): yObjectOrYCanvasObject is BoardItem =>
  (yObjectOrYCanvasObject as BoardItem).customSelectable !== undefined &&
  (yObjectOrYCanvasObject as BoardItem).configurations !== undefined &&
  (yObjectOrYCanvasObject as BoardItem).itemType !== undefined;

export const isBoardWebViewer = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is BoardWebViewer =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.WebViewer;

export const isBoardImage = (yObjectOrYCanvasObject: any): yObjectOrYCanvasObject is BoardImage =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.Image;

export const isBoardLine = (yObjectOrYCanvasObject: any): yObjectOrYCanvasObject is BoardLine =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.Line;

export const isBoardStickyNote = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is BoardStickyNote =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.StickyNote;

export const isBoardStickyNoteTextBox = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is fabric.Textbox =>
  yObjectOrYCanvasObject.parentBoardItem &&
  isBoardStickyNote(yObjectOrYCanvasObject.parentBoardItem);

export const isBoardFormula = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is BoardFormula =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.Formula;

export const isBoardImagesPdf = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is BoardImagesPdf =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.PDFImages;

export const isBoardRemoteVideo = (
  yObjectOrYCanvasObject: any,
): yObjectOrYCanvasObject is BoardRemoteVideo =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.RemoteVideo;

export const isBoardVideo = (yObjectOrYCanvasObject: any): yObjectOrYCanvasObject is BoardVideo =>
  isBoardItem(yObjectOrYCanvasObject) && yObjectOrYCanvasObject.itemType === ItemModel.Video;

enum SessionMetadataKey {
  CALL_ENDED = 'callEnded',
}

export function endCallKey(roomId?: string): string {
  return `${roomId}-${SessionMetadataKey.CALL_ENDED}`;
}

export interface SessionMetadata {
  [key: string]: boolean;
}

export interface Metadata {
  // Metadata per user
  // Key: userId
  userMetadata: UserMetadata;

  // Session level metadata, it can be used for any persisted state
  sessionMetadata: SessionMetadata;

  // Contains data with a lifetime the same as the socket connection
  // to the server
  // Key_1: socketId
  //      Key_2 userId
  temporaryUserMetadata: TemporaryUserMetadata;
}

export interface TemporaryUserMetadata {
  [key: string]: { [key: string]: TemporaryUserMetadataEntryYJS };
}
export interface UserMetadata {
  [key: string]: UserMetadataEntry;
}

export interface UserMetadataEntry {} // eslint-disable-line

/*
YJS Document Description
-------------------------
Plain text is constant
Text between <> is variable
-------------------------
ROOT (Y.Doc)
├─  <frame_uid> (Y.Arr<Y.Map>) # this is the array that holds the fabric Y.Maps
│   ├─ [entry] # A Y.Map containing the fabric data
├─  canvas-items-<frame_uid> (Y.Arr<Y.Map>) # this is the array that holds the canvas items Y.Maps
│   ├─ [entry] # A Y.Map containing the canvas item data
├─  frames<room_uid> (Y.Arr<Y.Map>) # this is the array that holds the frames of a particular room
│   ├─ [entry] # A Y.Map containing the canvas item data
├─  rooms (Y.arr<Y.Map>) # this is the array that holds the room Y.Maps
│   ├─ [entry] (Y.Map) # every entry in the frames Y.Arr is a Y.Map holds the value of the frame object
├─  userMetadata (Y.Map<UserMetadata>) # maps userId to the users metadata
│   ├─ [entry] (UserMetadata) # a plain object containing the metadata for that user
├─  sessionMetadata (Y.Map<unknown>) # session level metadata
├─  temporaryUserMetadata (Y.Map<TemporaryUserMetadata>) # maps socket.id to the users temporary metadata
│   ├─ [entry] (TemporaryUserMetadata) # an object that contains data whose lifetime is the same as the connection
---------------------------
ex: A space with 1 room, the room has two frames, each frame has two fabric items and 2 canvas items and there are two users in this space.
ROOT (Y.Doc)
{
  rooms: [
    {
      uid: room1_uid,
      name: room1
    },
  ],

  framesroom1_uid: [
    {
      uid: frame1_uid,
      name: frame 1
    },
    {
      uid: frame2_uid,
      name: frame 2
    }
  ]

  frame1_uid: [{fabricObject_1}, {fabricObject_2}]
  canvas-items-frame1_uid: [{canvasItem_1}, {canvasItem_2}]

  frame2_uid: [{fabricObject_1}, {fabricObject_2}]
  canvas-items-frame2_uid: [{canvasItem_1}, {canvasItem_2}]

  temporaryUserMetadata: {
    socketConnection_id1: {user_1 metadata}
    socketConnection_id2: {user_2 metadata}
  },
}
*/

export interface YSessionFlagConfigs {
  useFabricJson: boolean;
  breakoutRoomsEnabled: boolean;
  pendingStructsSyncThreshold: number | undefined;
  canvasItemsKeysDeletion: boolean;
  enableYSessionDiagnosticLogging: boolean;
}

export class YSessionBase {
  private readonly yDoc = new Y.Doc();
  private _cached = false;

  // Origin for all changes made to element arrays, used for undo / redo and sender if frameElementEvents
  protected readonly elementOrigin: string = uuidv4();

  // Origin for all changes made to frames, used to determine sender in frameEvents
  protected readonly frameOrigin: string = uuidv4();

  // Origin for all changes made to rooms, used to determine sender in roomEvents
  protected readonly roomOrigin: string = uuidv4();

  // origin for all changes made to board Folders, used to determine sender in foldersEvents
  protected readonly boardFoldersOrigin: string = uuidv4();

  private serverState?: Uint8Array;

  private readonly pendingStructsManager: PendingStructsManager;

  protected readonly undoManagers: Manager<Y.UndoManager>;

  public readonly yDocPendingChangesSubject = new Subject<Map<number, number> | undefined>();

  constructor(
    protected flagsConfigs: YSessionFlagConfigs,
    protected telemetry?: TelemetryService,
    protected readonly boardItemsEncoderDecoder?: BoardItemsEncoderDecoder,
  ) {
    this.pendingStructsManager = new PendingStructsManager(
      flagsConfigs.pendingStructsSyncThreshold,
    );

    // Create a manager that gets a new undo manager for a given frameUID when get is called, only track your own origin
    this.undoManagers = new Manager((frameUID) => {
      const yFrameElements = this.getYFrameElements(frameUID);
      const yCanvasItemsElements = this.getYFrameCanvasItems(frameUID);
      return new Y.UndoManager([yFrameElements, yCanvasItemsElements], {
        trackedOrigins: new Set([this.elementOrigin]),
        captureTimeout: 150,
        ignoreRemoteMapChanges: true,
      });
    });

    this.yDoc.on('afterTransaction', (trans: Y.Transaction): void => {
      if (
        trans.doc.store.pendingStructs &&
        this.pendingStructsManager.shouldSyncMissingUpdates(trans.doc.store.pendingStructs.missing)
      ) {
        this.yDocPendingChangesSubject.next(trans.doc.store.pendingStructs.missing);
      } else {
        this.yDocPendingChangesSubject.next(undefined);
      }
    });
  }

  public get pendingStructsSyncReachedThreshold$() {
    return this.pendingStructsManager.pendingStructsSyncReachedThreshold$;
  }

  // Apply the Y.js update and update the sever state
  public applyUpdate(update: SessionUpdate, origin: string): void {
    if (update.documentUpdate?.yjs_update) {
      const updateU8 = new Uint8Array(update.documentUpdate.yjs_update);
      Y.applyUpdate(this.yDoc, updateU8, origin);
    }
    if (update.serverStateVector) {
      this.updateServerState(update.serverStateVector);
    }
  }

  /**
   * @returns true if there is apending update that is removed after applied the update
   */
  public applyUpdateAndRemovePendingUpdates(update: SessionUpdate, origin: string): boolean {
    return this.yDoc.transact(() => {
      this.applyUpdate(update, origin);
      return this.clearPendingUpdates();
    }, origin);
  }

  private hasPendingUpdates() {
    return !!this.yDoc.store.pendingStructs;
  }

  private clearPendingUpdates() {
    if (this.hasPendingUpdates()) {
      this.pendingStructsManager.updateFirstLoadPendingStructs(
        this.yDoc.store.pendingStructs!.missing,
      );
      this.yDoc.store.pendingStructs = null;
      this.yDoc.store.pendingDs = null;
      return true;
    }
    return false;
  }

  public updateServerState(serverState: ArrayBuffer): void {
    this.serverState = new Uint8Array(serverState);
  }

  // Get the Y.Arr for the fabric elements
  protected getYFrameElements(frameUID: string): Y.Array<Y.Map<unknown> | YObject> {
    return this.yDoc.getArray(frameUID);
  }

  // Get the fabric elements as regular javascript objects
  protected getFrameElements(frameUID: string): YObject[] {
    return (
      this.getYFrameElements(frameUID)
        .toArray()
        // Because the entries in the YFrameElements may be a Map or a
        // JSON object, check to see if the
        .map(convertYFrameElementToObject)
    );
  }

  // Get the Y.Arr for the canvas items
  protected getYFrameCanvasItems(frameUid: string): Y.Array<Y.Map<any>> {
    const key = `canvas-items-${frameUid}`;
    return this.yDoc.getArray(key);
  }

  // Get the canvas items as regular javascript objects
  protected getFrameCanvasItems(frameUID: string): YCanvasItemObject[] {
    const canvasItems = this.getYFrameCanvasItems(frameUID)
      .toArray()
      .map((o) => o.toJSON() as YCanvasItemObject);
    for (const item of canvasItems) {
      if (item.position) {
        const positionMap = new Map();
        for (const [key, val] of Object.entries(item.position)) {
          positionMap.set(key, val);
        }
        item.position = positionMap;
      }
    }
    return canvasItems;
  }

  // Get the Y.Arr for the frames
  protected getYFrames(roomId: string): Y.Array<Y.Map<unknown>> {
    const key = `frames${roomId}`;
    return this.yDoc.getArray(key);
  }

  protected getYRooms(): Y.Array<Y.Map<unknown>> {
    return this.yDoc.getArray('rooms');
  }

  protected getYBoardFolders(): Y.Array<Y.Map<unknown>> {
    return this.yDoc.getArray('boardFolders');
  }

  protected getBoardFolders(): BoardFolder[] {
    return this.getYBoardFolders()
      .toArray()
      .map((f) => f.toJSON() as BoardFolder);
  }
  // Get the frames as regular javascript objects
  protected getFrames(roomId: string): Frame[] {
    return this.getYFrames(roomId)
      .toArray()
      .map((f) => f.toJSON() as Frame);
  }

  /**
   * Get frames that belong to a specific board folder
   * @param roomId The ID of the room containing the frames
   * @param boardFolderUid The UID of the board folder to filter by
   * @returns Array of frames associated with the board folder
   */
  protected getFramesByBoardFolder(roomId: string, boardFolderUid: string): Frame[] {
    return this.getFrames(roomId).filter((frame) => frame.boardFolderUid === boardFolderUid);
  }

  // Get the rooms as regular javascript objects
  protected getRooms(): Room[] {
    return this.getYRooms()
      .toArray()
      .map((f) => f.toJSON() as Room);
  }

  // Get the Y.Map for the session level metadata
  protected getYSessionMetadata(): Y.Map<unknown> {
    return this.yDoc.getMap('sessionMetadata');
  }

  // Get the session level metadata as a regular javascript object
  protected getSessionMetadata(): SessionMetadata {
    return this.getYSessionMetadata().toJSON();
  }

  // Get the Y.Map for the user level metadata
  // key: userId
  protected getYUserMetadata(): Y.Map<UserMetadataEntry> {
    return this.yDoc.getMap('userMetadata');
  }

  // Get the user level metadata as a regular javascript object
  protected getUserMetadata(): UserMetadata {
    return this.getYUserMetadata().toJSON();
  }

  // key: socket.id
  protected getYTemporaryUserMetadata(): Y.Map<TemporaryUserMetadataEntryYJS> {
    return this.yDoc.getMap('temporaryUserMetadata');
  }

  // Get the user level metadata as a regular javascript object
  protected getTemporaryUserMetadata(): { [key: string]: TemporaryUserMetadataEntryYJS } {
    return this.getYTemporaryUserMetadata().toJSON();
  }

  // Get the user socket level metadata as a regular javascript object
  public getTemporaryUserSocketMetadata(
    socketId: string,
  ): TemporaryUserMetadataEntryYJS | undefined {
    return this.getYTemporaryUserMetadata().get(socketId);
  }

  // Updates are always encoded against the last known server state
  // this is to ensure that we do not potentially drop data
  public getUpdate(): Uint8Array {
    return Y.encodeStateAsUpdate(this.yDoc, this.serverState);
  }

  /*
  Converts simple one level of depth objects to yMap
  TODO: (Mostafa) Should be deprecated and use objectToYMap_v2 instead
  */
  private objectToYMap<T extends object>(object: T): Y.Map<T> {
    const yMap = new Y.Map<T>();

    for (const [key, value] of Object.entries(object)) {
      yMap.set(key, value);
    }

    return yMap;
  }

  /* Converts complex objects to yMap*/
  private objectToYMap_v2<T extends object>(object: T): Y.Map<T> {
    const yMap = new Y.Map<T>();
    for (const [key, value] of Object.entries(object)) {
      this.setYMapItem(yMap, key, value);
    }

    return yMap;
  }

  private setYMapItem(yMap: Y.Map<any>, key: string, val: any): void {
    // yjs accepts only these constructors as value in the yMap.
    const constructors = [Number, Object, Boolean, Array, String, Uint8Array, Y.Doc];
    if (val) {
      if (val?.constructor === Map) {
        // convert to object;
        const tmp: { [key: string]: any } = {};
        val.forEach((v, k) => {
          tmp[k] = v;
        });
        val = tmp;
      }
      if (!constructors.includes(val.constructor)) {
        val.constructor = Object;
      }
    }
    yMap.set(key, val);
  }

  private updateYMapFromObject<T extends object>(original: Y.Map<unknown>, object: T): void {
    // first remove any keys that are missing from `object`
    const objectKeys = Object.keys(object);
    for (const key of original.keys()) {
      if (!objectKeys.includes(key)) {
        original.delete(key);
      }
    }
    // now update remaining keys + add any new ones from `object`
    for (const [key, value] of Object.entries(object)) {
      if (!original.has(key) || !isEqual(original.get(key), value)) {
        (original as any).set(key, value);
      }
    }
  }

  // Helper method to remove a board folder
  private removeBoardFolder(boardFolderUid: string): void {
    const yBoardFolder = this.getYBoardFolders();
    const idxToDelete: number[] = [];
    for (let i = yBoardFolder.length - 1; i >= 0; i--) {
      if (yBoardFolder.get(i).get('uid') === boardFolderUid) {
        idxToDelete.push(i);
        break;
      }
    }

    this.yDoc.transact(() => {
      for (const index of idxToDelete) {
        yBoardFolder.delete(index);
      }
    }, this.boardFoldersOrigin);
  }

  // add objects to the same board
  public addObjects(frameUID: string, objects: YObject[], trackUndoRedo = true): void {
    const yFrameElements = this.getYFrameElements(frameUID);
    this.yDoc.transact(
      () => {
        if (this.flagsConfigs.useFabricJson) {
          yFrameElements.push(objects);
        } else {
          yFrameElements.push(objects.map((o) => this.objectToYMap<any>(o)));
        }
      },
      trackUndoRedo ? this.elementOrigin : null,
    );
  }

  /*
   * Add each object to its board of creation
   */
  public addObjectsToDifferentBoards(
    objectsToBoardMap: Map<string | undefined, YObject[]>,
    trackUndoRedo = true,
  ): void {
    let yFrameElements: Y.Array<Y.Map<unknown> | YObject>;
    this.yDoc.transact(
      () => {
        for (const [boardUid, objects] of objectsToBoardMap) {
          yFrameElements = this.getYFrameElements(<string>boardUid);
          if (this.flagsConfigs.useFabricJson) {
            for (const objectAdded of objects) {
              yFrameElements.push([
                this.boardItemsEncoderDecoder!.encodeObjectIfNeeded(
                  objectAdded as FabricObject,
                ) as YObject,
              ]);
            }
          } else {
            for (const objectAdded of objects) {
              yFrameElements.push([objectAdded].map((o) => this.objectToYMap<any>(o)));
            }
          }
        }
      },
      trackUndoRedo ? this.elementOrigin : null,
    );
  }

  public addCanvasItems(
    frameUID: string,
    objects: YCanvasItemObject[],
    trackUndoRedo = true,
  ): void {
    const yFrameCanvasItems = this.getYFrameCanvasItems(frameUID);

    this.yDoc.transact(
      () => {
        objects.forEach((object) => {
          yFrameCanvasItems.push([this.objectToYMap_v2(object)]);
        });
      },
      trackUndoRedo ? this.elementOrigin : null,
    );
  }

  public modifyObjects(objectsToBoardMap: Map<string | undefined, YObject[]>): void {
    for (const [boardUid, objects] of objectsToBoardMap) {
      this.yDoc.transact(() => {
        // get yFrame for each board
        const yFrameElements = this.getYFrameElements(<string>boardUid);
        const elements = this.getFrameElements(<string>boardUid);
        if (!elements) {
          return;
        }
        for (const object of objects) {
          const uid = object.uid;
          const index = elements.findIndex((o) => o.uid === uid);
          if (index < 0) {
            continue;
          }
          const yFrameElement = yFrameElements.get(index);
          if (yFrameElement instanceof Y.Map) {
            this.updateYMapFromObject(yFrameElement, object);
          } else {
            yFrameElements.delete(index);
            yFrameElements.insert(index, [
              this.boardItemsEncoderDecoder!.encodeObjectIfNeeded(
                object as FabricObject,
              ) as YObject,
            ]);
          }
        }
      }, this.elementOrigin);
    }
  }

  public updateObjectsOrder(frameUID: string, objectUid: string, toFront: boolean): void {
    const yElements = this.getYFrameElements(frameUID);
    const elements = this.getFrameElements(frameUID);
    this.yDoc.transact(() => {
      for (let index = 0; index < elements.length; index++) {
        const element = elements[index];
        if (element['uid'] === objectUid) {
          yElements.delete(index);
          const toInsert = this.flagsConfigs.useFabricJson
            ? [element]
            : [this.objectToYMap<any>(element)];
          yElements.insert(toFront ? elements.length - 1 : 0, toInsert);
          break;
        }
      }
    }, this.elementOrigin);
  }

  public modifyCanvasItems(frameUID: string, objects: YCanvasItemObject[]): void {
    const yFrameElements = this.getYFrameCanvasItems(frameUID);
    const elements = this.getFrameCanvasItems(frameUID);

    this.yDoc.transact(() => {
      objects.forEach((object) => {
        const uid = object.uid;
        const index = elements.findIndex((o) => o.uid === uid);
        if (index < 0) {
          return;
        }
        const yFrameElement = yFrameElements.get(index);
        const objectKeys = Object.keys(object);
        const missingKeys: string[] = [];
        for (const key of yFrameElement.keys()) {
          if (!objectKeys.includes(key)) {
            if (this.flagsConfigs.enableYSessionDiagnosticLogging) {
              missingKeys.push(key);
            }
            if (this.flagsConfigs.canvasItemsKeysDeletion) {
              yFrameElement.delete(key);
            }
          }
        }
        if (missingKeys.length && this.flagsConfigs.enableYSessionDiagnosticLogging) {
          this.telemetry?.errorEvent('[YSession] Canvas item missing keys', {
            frameUID,
            uid,
            missingKeys: JSON.stringify(missingKeys),
            existingKeys: JSON.stringify(yFrameElement.keys()),
            object: JSON.stringify(object),
          });
        }
        // now update remaining keys + add any new ones from `object`
        for (const [key, value] of Object.entries(object)) {
          if (!yFrameElement.has(key) || !isEqual(yFrameElement.get(key), value)) {
            this.setYMapItem(yFrameElement, key, value);
          }
        }
      });
    }, this.elementOrigin);
  }

  /*
   * When an object is removed find its index and remove it from the yjs state
   */
  public removeObjects(objectsToBoardMap: Map<string | undefined, YObject[]>): void {
    const uidSet = this.getUidsSet(objectsToBoardMap);
    const boardUids = objectsToBoardMap.keys();
    // for each Frame we have, check the existing uids to be removed
    for (const boardUid of boardUids) {
      const yFrameElements = this.getYFrameElements(<string>boardUid);
      const elements = this.getFrameElements(<string>boardUid);

      let indices: number[] = [];

      for (const [index, element] of elements.entries()) {
        if (element.uid && uidSet.has(element.uid)) {
          indices.push(index);
        }
      }

      // removing multiple elements by index can cause "index out of bounds" issues. In order to avoid this,
      // we sort the indices in reverse numerical order so we remove "from the back", thus removed indices will not cause issues
      // for the elements "in front of" the removed element
      indices = indices.sort((a, b) => b - a);

      this.yDoc.transact(() => {
        for (const index of indices) {
          if (index < 0) {
            continue;
          }
          yFrameElements.delete(index);
        }
      }, this.elementOrigin);
    }
  }

  public removeCanvasItems(frameUID: string, objects: YCanvasItemObject[]): void {
    const yFrameElements = this.getYFrameCanvasItems(frameUID);
    const elements = this.getFrameCanvasItems(frameUID);

    const uidSet = new Set<string>();
    for (const object of objects) {
      if (object.uid) {
        uidSet.add(object.uid);
      }
    }

    let indices: number[] = [];
    for (const [index, element] of elements.entries()) {
      if (element.uid && uidSet.has(element.uid)) {
        indices.push(index);
      }
    }

    // removing multiple elements by index can cause "index out of bounds" issues. In order to avoid this,
    // we sort the indices in reverse numerical order so we remove "from the back", thus removed indices will not cause issues
    // for the elements "in front of" the removed element
    indices = indices.sort((a, b) => b - a);

    this.yDoc.transact(() => {
      for (const index of indices) {
        if (index < 0) {
          continue;
        }
        yFrameElements.delete(index);
      }
    }, this.elementOrigin);
  }

  public isConvertedToRooms(): boolean {
    return this.getYDoc().getArray('rooms').length !== 0;
  }

  public addBoardFolder(boardFolder: BoardFolder): void {
    const yBoardFolders = this.getYBoardFolders();
    this.yDoc.transact(() => {
      yBoardFolders.push([this.objectToYMap<any>(boardFolder)]);
    }, this.boardFoldersOrigin);
  }

  public addRooms(rooms: Room[]): void {
    const yRooms = this.getYRooms();
    const newYRooms: Y.Map<any>[] = [];
    for (const room of rooms) {
      newYRooms.push(this.objectToYMap_v2<any>(room));

      const frame = new Frame({
        name: 'Board 1',
        frameType: FrameType.WHITEBOARD,
      });
      const yFrames = this.getYFrames(room.uid);
      this.yDoc.transact(() => {
        yFrames.push([this.objectToYMap<any>(frame)]);
      });
    }
    this.yDoc.transact(() => {
      yRooms.push(newYRooms);
    }, this.roomOrigin);
  }

  public deleteRooms(uids: string[]): void {
    const yRooms = this.getYRooms();
    const indices: number[] = [];
    const roomUIDs = new Set<string>(uids);
    for (let i = 0; i < yRooms.length; ++i) {
      console.log('msg', yRooms.get(i).get('uid'), roomUIDs);
      if (yRooms.get(i).get('uid') && roomUIDs.has(yRooms.get(i).get('uid') as string)) {
        indices.push(i);
      }
    }

    if (indices.length > 0) {
      for (let i = indices.length - 1; i >= 0; --i) {
        this.yDoc.transact(() => {
          yRooms.delete(indices[i]);
        }, this.roomOrigin);
      }
    }
  }

  public modifyRoom(room: Room): void {
    const yRooms = this.getYRooms();

    this.yDoc.transact(() => {
      yRooms.forEach((yRoom, idx) => {
        if (yRoom.get('uid') === room.uid) {
          for (const [key, value] of Object.entries(room)) {
            if (!isEqual(yRoom.get(key), value)) {
              this.setYMapItem(yRoom, key, value);
            }
          }
        }
      });
    }, this.roomOrigin);
  }

  public moveFrameBetweenRooms(movedBoardsPlacement: MovedBoardsPlacement[]): void {
    const transactionData: {
      fromRoomYFrames: Y.Array<Y.Map<any>>;
      toRoomYFrames: Y.Array<Y.Map<any>>;
      removeAtIndices: number[];
      toMoveYFrames: Y.Map<any>[];
    }[] = [];

    movedBoardsPlacement.forEach(({ fromRoomId, toRoomId, framesIds }) => {
      const fromRoomFrames = this.getFrames(fromRoomId);
      const toRoomYFrames = this.getYFrames(toRoomId);
      const removeAtIndices: number[] = [];
      const toMoveFrames: Y.Map<any>[] = [];

      fromRoomFrames.forEach((frame, idx) => {
        if (frame.uid && framesIds.has(frame.uid)) {
          // reset board folder to frame if we're moving it to another room
          frame.boardFolderUid = undefined;
          toMoveFrames.push(this.objectToYMap_v2(frame));
          removeAtIndices.push(idx);
        }
      });

      transactionData.push({
        fromRoomYFrames: this.getYFrames(fromRoomId),
        toRoomYFrames: toRoomYFrames,
        removeAtIndices: removeAtIndices,
        toMoveYFrames: toMoveFrames,
      });
    });

    this.yDoc.transact(() => {
      transactionData.forEach(
        ({ fromRoomYFrames, toRoomYFrames, removeAtIndices, toMoveYFrames }) => {
          toRoomYFrames.push(toMoveYFrames);
          for (let i = removeAtIndices.length - 1; i >= 0; --i) {
            fromRoomYFrames.delete(removeAtIndices[i]);
          }
        },
      );
    }, this.frameOrigin);
  }

  public addFrame(roomId: string, frame: Frame): void {
    // Ensure that this is a valid object
    const yFrames = this.getYFrames(roomId);

    this.yDoc.transact(() => {
      yFrames.push([this.objectToYMap<any>(frame)]);
    }, this.frameOrigin);
  }

  public addFrames(roomId: string, frames: Frame[]): void {
    const yFrames = this.getYFrames(roomId);

    const newYFrames = frames.map((frame) => this.objectToYMap<any>(frame));

    if (newYFrames.length) {
      this.yDoc.transact(() => {
        yFrames.push(newYFrames);
      }, this.frameOrigin);
    }
  }

  public modifyFrame(roomId: string, frame: Frame): void {
    const yFrames = this.getYFrames(roomId);

    this.yDoc.transact(() => {
      yFrames.forEach((yFrame, idx) => {
        if (yFrame.get('uid') === frame.uid) {
          for (const [key, value] of Object.entries(frame)) {
            if (!isEqual(yFrame.get(key), value)) {
              this.setYMapItem(yFrame, key, value);
            }
          }
        }
      });
    }, this.frameOrigin);
  }

  public modifyBoardFolder(boardFolder: BoardFolder): void {
    let yBoardFolder: Y.Map<unknown> | undefined;
    const yBoardFolders = this.getYBoardFolders();

    for (let i = 0; i < yBoardFolders.length; i++) {
      if (yBoardFolders.get(i).get('uid') === boardFolder.uid) {
        yBoardFolder = yBoardFolders.get(i);
        break;
      }
    }

    if (!yBoardFolder) {
      return;
    }

    this.yDoc.transact(() => {
      for (const [key, value] of Object.entries(boardFolder)) {
        if (!isEqual(yBoardFolder?.get(key), value)) {
          this.setYMapItem(yBoardFolder as Y.Map<unknown>, key, value);
        }
      }
    }, this.boardFoldersOrigin);
  }

  /**
   * Move the Frame to the new position
   * old position is removed and frames will be reordered
   * the new frame will be position in the new toIndex
   * @param roomId
   * @param frame
   * @param fromIndex
   * @param toIndex
   */
  public moveFrame(roomId: string, frame: Frame, fromIndex: number, toIndex: number): boolean {
    const yFrames = this.getYFrames(roomId);
    // prevent the move if the frames changed just before applying the move
    if (yFrames.get(fromIndex).get('uid') !== frame.uid) {
      return false;
    }
    this.yDoc.transact(() => {
      yFrames.delete(fromIndex);
      yFrames.insert(toIndex, [this.objectToYMap<any>(frame)]);
    }, this.frameOrigin);

    return true;
  }

  updateFramePrivacy(roomId: string, frame: Frame): void {
    const yFrames = this.getYFrames(roomId);
    this.yDoc.transact(() => {
      yFrames.forEach((yFrame) => {
        if (yFrame.get('uid') === frame.uid) {
          yFrame.set('privacy', frame.privacy);
          if (frame.privacy === FramePrivacy.PUBLIC) {
            yFrame.set('privateUsers', []);
          }
          if (frame.privacy === FramePrivacy.PRIVATE) {
            yFrame.set('privateUsers', frame.privateUsers);
          }
        }
      });
    }, this.frameOrigin);
  }

  updateFrameBoardFolder(roomId: string, frame: Frame): void {
    let yFrame: Y.Map<unknown> | undefined;
    const yFrames = this.getYFrames(roomId);

    for (let i = 0; i < yFrames.length; i++) {
      if (yFrames.get(i).get('uid') === frame.uid) {
        yFrame = yFrames.get(i);
        break;
      }
    }

    if (!yFrame) {
      return;
    }

    this.yDoc.transact(() => {
      yFrame?.set('boardFolderUid', frame.boardFolderUid);
    }, this.frameOrigin);
  }

  /**
   * change the frame lock state inside the yjs document
   * if it is unlocked, lock it else unlock it
   * locked frames can be only edited with hosts/owners
   * @param roomId
   * @param frame
   */
  updateFrameLockState(roomId: string, frame: Frame): void {
    const yFrames = this.getYFrames(roomId);
    this.yDoc.transact(() => {
      yFrames.forEach((yFrame) => {
        if (yFrame.get('uid') === frame.uid) {
          yFrame.set('locked', frame.locked);
        }
      });
    }, this.frameOrigin);
  }

  /**
   * change the frame background pattern and color inside the yjs document
   * @param roomId
   * @param frame
   */
  updateFrameBackground(roomId: string, frame: Frame): void {
    const yFrames = this.getYFrames(roomId);
    this.yDoc.transact(() => {
      yFrames.forEach((yFrame) => {
        if (yFrame.get('uid') === frame.uid) {
          yFrame.set('backgroundColor', frame.backgroundColor);
          yFrame.set('backgroundPattern', frame.backgroundPattern);
        }
      });
    }, this.frameOrigin);
  }

  public clearFrameAnnotations(roomId: string, frameUid: string): void {
    const yFrameElements = this.getYFrameElements(<string>frameUid);

    this.undoManagers.get(frameUid).clear(true, true);

    const elements = this.getFrameElements(<string>frameUid);
    const decodedElements =
      this.boardItemsEncoderDecoder?.decodeFabricItems(elements as FabricObject[]) ?? [];
    const uidSet = new Set<string>();

    for (const element of decodedElements) {
      // annotations here are scribbles and highlights which are the only items that the type are 'path'
      if (itemIsScribbleOrHighlight(element) && element.uid) {
        uidSet.add(element.uid);
      }
    }

    let indices: number[] = [];
    for (const [index, element] of elements.entries()) {
      if (element.uid && uidSet.has(element.uid)) {
        indices.push(index);
      }
    }

    // removing multiple elements by index can cause "index out of bounds" issues. In order to avoid this,
    // we sort the indices in reverse numerical order so we remove "from the back", thus removed indices will not cause issues
    // for the elements "in front of" the removed element
    indices = indices.sort((a, b) => b - a);

    this.yDoc.transact(() => {
      for (const index of indices) {
        if (index < 0) {
          continue;
        }
        yFrameElements.delete(index);
      }
    }, this.frameOrigin);
  }

  public clearFrameContent(roomId: string, frameUid: string): void {
    const fabricItems = this.getYFrameElements(frameUid);
    const canvasItems = this.getYFrameCanvasItems(frameUid);
    this.undoManagers.get(frameUid).clear(true, true);

    this.yDoc.transact(() => {
      fabricItems.delete(0, fabricItems.length);
      canvasItems.delete(0, canvasItems.length);
    }, this.frameOrigin);
  }

  public removeFrame(roomId: string, frameUid: string): void {
    const yFrames = this.getYFrames(roomId);
    const fabricItems = this.getYFrameElements(frameUid);
    const canvasItems = this.getYFrameCanvasItems(frameUid);
    const idxToDelete: number[] = [];
    // since we can't avoid frame duplication there may be multiple frames with the same frame id
    // removing multiple elements by index can cause "index out of bounds" issues. In order to avoid this,
    // we sort the indices in reverse numerical order so we remove "from the back", thus removed indices will not cause issues
    // for the elements "in front of" the removed element
    for (let i = yFrames.length - 1; i >= 0; i--) {
      if (yFrames.get(i).get('uid') === frameUid) {
        idxToDelete.push(i);
      }
    }
    this.yDoc.transact(() => {
      for (const index of idxToDelete) {
        yFrames.delete(index);
      }
      fabricItems.delete(0, fabricItems.length);
      canvasItems.delete(0, canvasItems.length);
    }, this.frameOrigin);
  }

  /**
   * Removes a board folder and all frames associated with it
   * @param roomId The ID of the room containing the frames
   * @param boardFolder The board folder to be removed
   */
  public removeBoardFolderWithFrames(roomId: string, boardFolder: BoardFolder): void {
    // Get frames associated with this board folder
    const framesWithFolder = this.getFramesByBoardFolder(roomId, boardFolder.uid);

    this.yDoc.transact(() => {
      // Remove frames associated with the board folder
      if (framesWithFolder.length) {
        for (const frame of framesWithFolder) {
          if (frame) {
            this.removeFrame(roomId, frame.uid);
          }
        }
      }

      // Remove the board folder itself
      this.removeBoardFolder(boardFolder.uid);
    }, this.boardFoldersOrigin);
  }

  /**
   * Resets all boards and folders for a room - removes all board folders,
   * keeps the first frame (index 0) renamed as "Board 1" with cleared content, and removes all other frames
   * @param roomId The ID of the room to reset
   * @param newFrameName The name for the kept frame (default: "Board 1")
   * @returns The kept and renamed frame
   */
  public resetRoomBoardsAndFolders(roomId: string, newFrameName: string): Frame | null {
    const yFrames = this.getYFrames(roomId);
    const yBoardFolders = this.getYBoardFolders();

    if (yFrames.length === 0 && yBoardFolders.length === 0) {
      return null;
    }

    const newFrame = new Frame({
      name: newFrameName,
      frameType: FrameType.WHITEBOARD,
    });

    this.yDoc.transact(() => {
      // Add a new frame at index 0 to keep having a valid board at this position
      yFrames.insert(0, [this.objectToYMap<any>(newFrame)]);

      // Remove all board folders for this room
      this.removeBoardFoldersForRoom(roomId);

      // Remove all other frames (keeping only the first one)
      this.removeFramesExcept(roomId, newFrame.uid);
    }, this.frameOrigin);

    return newFrame;
  }

  private removeBoardFoldersForRoom(roomId: string): void {
    const yBoardFolders = this.getYBoardFolders();
    const folderIndicesToDelete: number[] = [];

    for (let i = yBoardFolders.length - 1; i >= 0; i--) {
      if (yBoardFolders.get(i).get('roomUid') === roomId) {
        folderIndicesToDelete.push(i);
      }
    }

    for (const index of folderIndicesToDelete) {
      yBoardFolders.delete(index);
    }
  }

  private removeFramesExcept(roomId: string, keepFrameUid: string): void {
    const yFrames = this.getYFrames(roomId);
    const indicesToDelete: number[] = [];

    for (let i = yFrames.length - 1; i >= 0; i--) {
      const frameUid = yFrames.get(i).get('uid');
      if (frameUid !== keepFrameUid) {
        indicesToDelete.push(i);
        // Clear content of frames being removed
        const fabricItemsToRemove = this.getYFrameElements(frameUid as string);
        const canvasItemsToRemove = this.getYFrameCanvasItems(frameUid as string);
        fabricItemsToRemove.delete(0, fabricItemsToRemove.length);
        canvasItemsToRemove.delete(0, canvasItemsToRemove.length);
      }
    }

    for (const index of indicesToDelete) {
      yFrames.delete(index);
    }
  }

  // Returns an update if there was a change, otherwise it returns undefined
  public undoRedo(frameUID: string, type: UndoEnum): { updatedIds: string[] } | undefined {
    const undoManager = this.undoManagers.get(frameUID);

    const fabricElementsBeforeUndo = this.getFrameElements(frameUID);
    const canvasItemsBeforeUndo = this.getFrameCanvasItems(frameUID);

    switch (type) {
      case UndoEnum.UNDO:
        undoManager.undo();
        break;
      case UndoEnum.REDO:
        undoManager.redo();
        break;
      default:
        return;
    }

    const fabricElementsAfterUndo = this.getFrameElements(frameUID);
    const canvasItemsAfterUndo = this.getFrameCanvasItems(frameUID);

    const changedFabricItemsUIDs = this.getUIDDiff(
      fabricElementsBeforeUndo,
      fabricElementsAfterUndo,
    );
    const changedCanvasItemsUIDs = this.getUIDDiff(canvasItemsBeforeUndo, canvasItemsAfterUndo);

    // If no objects have changed then there was no op
    if (!changedFabricItemsUIDs.length && !changedCanvasItemsUIDs.length) {
      return;
    }

    return {
      updatedIds: changedFabricItemsUIDs,
    };
  }

  // Get a set of uids for all objects in the map
  private getUidsSet(objectsMap: Map<string | undefined, YObject[]>): Set<any> {
    const resultSet = new Set<any>();

    objectsMap.forEach((values) => {
      values.forEach((value) => {
        resultSet.add(value.uid);
      });
    });
    return resultSet;
  }

  // Gets the different uids from two arrays of fabric objects
  private getUIDDiff(s1: any[], s2: any[]): string[] {
    // Holds the mapping from uniqueID to versionID
    // O(n,m) way to check for object deltas
    const uidMap1: { [key: string]: string } = {};
    const uidMap2: { [key: string]: string } = {};
    const changedUIDs: Set<string> = new Set();

    s1.forEach((s1Obj) => {
      uidMap1[s1Obj.uid] = s1Obj.vuid;
    });

    s2.forEach((s2Obj) => {
      const uid = s2Obj.uid;
      uidMap2[uid] = s2Obj.vuid;

      if (!(uid in uidMap1) || uidMap1[uid] !== s2Obj.vuid) {
        changedUIDs.add(uid);
      }
    });

    s1.forEach((s1Obj) => {
      const uid = s1Obj.uid;
      if (!(uid in uidMap2)) {
        changedUIDs.add(uid);
      }
    });

    return Array.from(changedUIDs);
  }

  public modifySessionMetadata(metadata: Partial<SessionMetadata>): void {
    const ySessionMetadata = this.getYSessionMetadata();

    this.yDoc.transact(() => {
      for (const [key, value] of Object.entries(metadata)) {
        ySessionMetadata.set(key, value);
      }
    });
  }

  public modifyTemporaryUserMetadata(
    socketId: string,
    userId: string,
    temporaryUserMetadata: Partial<TemporaryUserMetadataEntryBase>,
  ): void {
    const yUserMetadata = this.getYTemporaryUserMetadata();

    const currentTemporaryUserMetadata =
      yUserMetadata.get(socketId) || ({} as TemporaryUserMetadataEntryYJS);

    // Only update the changed properties
    for (const [key, value] of Object.entries(temporaryUserMetadata)) {
      currentTemporaryUserMetadata[key as keyof TemporaryUserMetadataEntryBase] = value;
    }

    currentTemporaryUserMetadata.userId = userId;
    yUserMetadata.set(socketId, currentTemporaryUserMetadata);
  }

  public getYDoc(): Y.Doc {
    return this.yDoc;
  }

  public get cached(): boolean {
    return this._cached;
  }

  public set cached(cached: boolean) {
    this._cached = cached;
  }

  public deduplicateYFrames(frames?: Record<string, Frame[]>): boolean {
    return Object.keys(frames ?? {})
      .map((roomUid) => this.deduplicateYFramesForRoom(roomUid))
      .some((didFrameDuplicationOccur) => didFrameDuplicationOccur);
  }

  private deduplicateYFramesForRoom(roomId: string) {
    const yFrames = this.getYFrames(roomId);

    return deduplicateUniqueYArray(
      yFrames,
      (yFrame) => yFrame.get('uid') as string,
      (yArray, lastIndex, duplicatedValue) => {
        const lastFrame = yArray.get(lastIndex);
        const combinedProps = new Frame({
          ...duplicatedValue.toJSON(),
          ...lastFrame.toJSON(),
        });

        for (const [key, value] of Object.entries(combinedProps)) {
          lastFrame.set(key, value);
        }
      },
    );
  }
}

export function convertYFrameElementToObject(yFrameElement: Y.Map<unknown> | YObject): YObject {
  if (yFrameElement instanceof Y.Map) {
    return yFrameElement.toJSON() as YObject;
  } else {
    return yFrameElement;
  }
}

export class YSession extends YSessionBase {
  public updateObservable$: Observable<Partial<ISpaceYjs>>;

  // These sets are used to ensure that we only subscribe to frames/rooms once
  // when creating the updateObservable$
  private readonly _roomSubscriptions: Set<string> = new Set();
  private readonly _frameSubscriptions: Set<string> = new Set();

  constructor(
    flagsConfigs: YSessionFlagConfigs,
    telemetry?: TelemetryService,
    boardItemsEncoderDecoder?: BoardItemsEncoderDecoder,
  ) {
    super(flagsConfigs, telemetry, boardItemsEncoderDecoder);
    this.updateObservable$ = this.createUpdateObservable();
  }

  /**
   * Creates an observables that emits when the store should be updated from a Y.Doc
   */
  private createUpdateObservable(): Observable<Partial<ISpaceYjs>> {
    type UpdateObservable = Observable<Partial<ISpaceYjs>>;

    // Gets an observable for a frame if it has not already been created
    const getFramesObservable = (roomUID: string): UpdateObservable => {
      if (this._roomSubscriptions.has(roomUID)) {
        return EMPTY;
      }
      this._roomSubscriptions.add(roomUID);

      return this.frames$(roomUID).pipe(
        map(({ frames, local, lastActionIsLocal }) => ({
          frames: { [roomUID]: frames },
          lastUpdateLocal: local,
          lastActionIsLocal: lastActionIsLocal,
        })),
      );
    };

    // Get an observable for the canvas objects if it has not already been created
    // note: this deals with both canvas items and fabric items
    const getCanvasObjectsObservable = (frameUID: string): UpdateObservable => {
      if (this._frameSubscriptions.has(frameUID)) {
        return EMPTY;
      }
      this._frameSubscriptions.add(frameUID);

      return this.canvasItems$(frameUID).pipe(
        map(({ canvasItems, local, lastActionIsLocal }) => ({
          canvasItems: { [frameUID]: canvasItems },
          lastUpdateLocal: local,
          lastActionIsLocal: lastActionIsLocal,
        })),
        mergeWith(
          this.fabricItems$(frameUID).pipe(
            map(({ fabricItems, local, lastActionIsLocal }) => ({
              fabricItems: { [frameUID]: fabricItems },
              lastUpdateLocal: local,
              lastActionIsLocal: lastActionIsLocal,
            })),
          ),
        ),
      );
    };

    const metadata$: UpdateObservable = this.metadata$().pipe(
      map(({ metadata, local }) => ({ metadata, lastUpdateLocal: local })),
    );

    const boardFolders$: UpdateObservable = this.boardFolders$().pipe(
      map(({ boardFolders, local }) => ({ boardFolders, lastUpdateLocal: local })),
    );

    // If breakout rooms is disabled we need to add a "dummy" room to start off the
    // observable chain.
    // note: uid = "" is the UID used when breakout rooms is disabled
    const defaultRoom = new Room({ uid: '' });

    const rooms$: UpdateObservable = (
      this.flagsConfigs.breakoutRoomsEnabled
        ? this.rooms$()
        : new BehaviorSubject({ rooms: [defaultRoom], local: false })
    ).pipe(
      map(({ rooms, local }) => ({ rooms, lastUpdateLocal: local })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const frames$ = rooms$.pipe(
      mergeMap(({ rooms }) => {
        const observables = rooms?.map((room) => getFramesObservable(room.uid)) ?? [];
        return merge(...observables);
      }),
      // We need to share rooms because it is subscribed to twice
      // once for canvasObjects$ and once for the final merge
      share(),
    );

    const canvasObjects$ = frames$.pipe(
      mergeMap(({ frames }) => {
        if (!frames) {
          return EMPTY;
        }
        const boards = Object.values(frames).flat(1);

        // Runs inside a Y.js observer: batch the per-board array creation before subscribing
        this.materializeFrameYArrays(boards.map((board) => board.uid));

        const observables = boards.map((board) => getCanvasObjectsObservable(board.uid));

        return merge(...observables);
      }),
    );

    return merge(metadata$, canvasObjects$, frames$, rooms$, boardFolders$);
  }

  /**
   * Materialize the fabric elements and canvas items Y.Arrays for the given frames in a single transaction.
   * Frames already in _frameSubscriptions have their arrays set up and are skipped, and no transaction
   * is opened when none are new.
   *
   * The first getArray() for a key integrates the type, which opens its own transaction.
   * Outside an observer that is harmless: the transaction completes before getArray() returns.
   * But the frame subscriptions run synchronously inside a Y.js observer callback (e.g. the frames
   * observer firing after the space snapshot is applied). Transactions opened there cannot run
   * immediately; Y.js queues them and processes the queue recursively (one cleanupTransactions
   * stack frame per transaction). Calling getArray() per frame from inside the observer therefore
   * queued 2 transactions per board, and spaces with thousands of boards hit
   * "RangeError: Maximum call stack size exceeded". Wrapping the calls in one transaction makes them
   * join it, so only a single transaction is queued.
   *
   * Must cover every array read per frame by the frame subscriptions (see fabricItems$ and canvasItems$).
   */
  private materializeFrameYArrays(frameUIDs: string[]): void {
    const newFrameUIDs = frameUIDs.filter((frameUID) => !this._frameSubscriptions.has(frameUID));
    if (newFrameUIDs.length === 0) {
      return;
    }

    this.getYDoc().transact(() => {
      for (const frameUID of newFrameUIDs) {
        this.getYFrameElements(frameUID);
        this.getYFrameCanvasItems(frameUID);
      }
    });
  }

  private fabricItems$(
    frameUID: string,
  ): Observable<{ fabricItems: YObject[]; local: boolean; lastActionIsLocal: boolean }> {
    this.undoManagers.get(frameUID);

    const yFrameElements = this.getYFrameElements(frameUID);

    const func = (
      local: boolean,
      lastActionIsLocal: boolean,
    ): { fabricItems: YObject[]; local: boolean; lastActionIsLocal: boolean } =>
      // Depending on what the origin was we can determine if it was made locally or remote
      ({ fabricItems: this.getFrameElements(frameUID), local, lastActionIsLocal });
    // Defer will call the callback when there is a subscriber to this function, this ensures that
    // the startWith value is always fresh
    return defer(() =>
      createYObservable(yFrameElements, func).pipe(
        startWith({
          fabricItems: this.getFrameElements(frameUID),
          local: false,
          lastActionIsLocal: false,
        }),
      ),
    );
  }

  private canvasItems$(
    frameUID: string,
  ): Observable<{ canvasItems: YCanvasItemObject[]; local: boolean; lastActionIsLocal: boolean }> {
    this.undoManagers.get(frameUID);

    const yFrameElements = this.getYFrameCanvasItems(frameUID);
    const func = (
      local: boolean,
      lastActionIsLocal: boolean,
    ): { canvasItems: YCanvasItemObject[]; local: boolean; lastActionIsLocal: boolean } =>
      // Depending on what the origin was we can determine if it was made locally or remote
      ({ canvasItems: this.getFrameCanvasItems(frameUID), local, lastActionIsLocal });

    // Defer will call the callback when there is a subscriber to this function, this ensures that
    // the startWith value is always fresh
    return defer(() =>
      createYObservable(yFrameElements, func).pipe(
        startWith({
          canvasItems: this.getFrameCanvasItems(frameUID),
          local: false,
          lastActionIsLocal: false,
        }),
      ),
    );
  }

  private frames$(
    roomId: string,
  ): Observable<{ frames: Frame[]; local: boolean; lastActionIsLocal: boolean }> {
    const yFrames = this.getYFrames(roomId);

    const func = (
      local: boolean,
      lastActionIsLocal: boolean,
    ): { frames: Frame[]; local: boolean; lastActionIsLocal: boolean } => ({
      frames: this.getFrames(roomId),
      local,
      lastActionIsLocal,
    });

    // Use defer tp ensure that the value of startWith is fresh
    return defer(() =>
      createYObservable(yFrames, func).pipe(
        startWith({ frames: this.getFrames(roomId), local: false, lastActionIsLocal: false }),
        filter(({ frames }) => frames.length > 0),
      ),
    );
  }

  private rooms$(): Observable<{ rooms: Room[]; local: boolean }> {
    const yRooms = this.getYRooms();

    const func = (local: boolean) => ({ rooms: this.getRooms(), local });

    // Use defer tp ensure that the value of startWith is fresh
    return defer(() =>
      createYObservable(yRooms, func).pipe(
        startWith({ rooms: this.getRooms(), local: false }),
        filter(({ rooms }) => rooms.length > 0),
      ),
    );
  }

  private boardFolders$(): Observable<{ boardFolders: BoardFolder[]; local: boolean }> {
    const yBoardFolders = this.getYBoardFolders();

    const func = (local: boolean) => ({ boardFolders: this.getBoardFolders(), local });

    // Use defer tp ensure that the value of startWith is fresh
    return defer(() =>
      createYObservable(yBoardFolders, func).pipe(
        startWith({ boardFolders: this.getBoardFolders(), local: false }),
      ),
    );
  }

  private metadata$(): Observable<{ metadata: Metadata; local: boolean }> {
    const yUserMetadata = this.getYUserMetadata();
    const yTemporaryUserMetadata = this.getYTemporaryUserMetadata();
    const ySessionMetadata = this.getYSessionMetadata();

    const func1 = (local: boolean) => ({ metadata: this.getUserMetadata(), local });
    const func2 = (local: boolean) => ({ metadata: this.getSessionMetadata(), local });
    const func3 = (local: boolean) => ({ metadata: this.getTemporaryUserMetadata(), local });

    const o1 = createYObservable(yUserMetadata, func1).pipe(timestamp());
    const o2 = createYObservable(ySessionMetadata, func2).pipe(timestamp());
    const o3 = createYObservable(yTemporaryUserMetadata, func3).pipe(timestamp());

    return defer(() =>
      combineLatest(
        [
          o1.pipe(startWith({ value: func1(false), timestamp: Date.now() })),
          o2.pipe(startWith({ value: func2(false), timestamp: Date.now() })),
          o3.pipe(startWith({ value: func3(false), timestamp: Date.now() })),
        ],
        (userMetadata, sessionMetadata, temporaryUserMetadata) => {
          const temporaryUserMetadataFormatted: {
            [key: string]: { [key: string]: TemporaryUserMetadataEntryYJS };
          } = {};

          for (const [socketId, tempData] of Object.entries(temporaryUserMetadata.value.metadata)) {
            const userId = tempData.userId;
            if (!userId) {
              continue;
            }

            if (!(userId in temporaryUserMetadataFormatted)) {
              temporaryUserMetadataFormatted[userId] = {};
            }
            temporaryUserMetadataFormatted[userId][socketId] = tempData;
          }

          // Get the local from the largest timestampt
          const local = [userMetadata, sessionMetadata, temporaryUserMetadata].sort(
            (a, b) => b.timestamp - a.timestamp,
          )[0].value.local;

          return {
            metadata: {
              userMetadata: userMetadata.value.metadata,
              sessionMetadata: sessionMetadata.value.metadata,
              temporaryUserMetadata: temporaryUserMetadataFormatted,
            },
            local,
          };
        },
      ),
    );
  }

  public getUndoStackObservable(
    frameUID: string,
  ): Observable<{ undoStack: number; redoStack: number }> {
    const undoManager = this.undoManagers.get(frameUID);

    const subject: Subject<{ undoStack: number; redoStack: number }> = new Subject();

    const func1 = () => ({
      undoStack: undoManager.undoStack.length || 0,
      redoStack: undoManager.redoStack.length || 0,
    });

    const func2 = () => {
      subject.next(func1());
    };

    undoManager.on('stack-item-added', func2);
    undoManager.on('stack-item-popped', func2);

    const observable = subject.pipe(
      finalize(() => {
        undoManager.off('stack-item-added', func2);
        undoManager.off('stack-item-popped', func2);
      }),
      share(),
      startWith(func1()),
    );

    return observable;
  }

  public clearTemporaryUserMetadata = (socketId: string): void => {
    const yTemporaryUserMetadata = this.getYTemporaryUserMetadata();
    yTemporaryUserMetadata.set(socketId, {} as TemporaryUserMetadataEntryYJS);
  };

  public removeTemporaryUserMetadata = (socketId: string): void => {
    const yTemporaryUserMetadata = this.getYTemporaryUserMetadata();
    yTemporaryUserMetadata.delete(socketId);
  };

  public getClientStateVector(): Uint8Array {
    return Y.encodeStateVector(this.getYDoc());
  }

  public getYDocSize(): number {
    return Y.encodeStateAsUpdate(this.getYDoc()).byteLength;
  }

  public getYDocAsJSON(): { [key: string]: any } {
    const YDocKeys = this.getYDoc().share.keys();
    const YDocJSON: { [key: string]: any } = {};
    for (const key of YDocKeys) {
      YDocJSON[key] = this.getYDoc().get(key).toJSON();
    }
    return YDocJSON;
  }

  public getYDocAsBase64(): string {
    const documentState = this.getYDocAsBinary();
    return fromUint8Array(documentState); // Transform Uint8Array to a Base64-String
  }

  public getYDocAsBinary(): Uint8Array {
    const newYDoc = new Y.Doc();
    const documentState = Y.encodeStateAsUpdate(this.getYDoc());
    const blackListedKeys = ['userMetadata', 'temporaryUserMetadata', 'sessionMetadata'];

    Y.applyUpdate(newYDoc, documentState);

    for (const blackListedKey of blackListedKeys) {
      const yMap = newYDoc.getMap(blackListedKey);
      const yMapKey = yMap.keys();

      for (const key of yMapKey) {
        yMap.delete(key);
      }
    }

    const newDocumentState = Y.encodeStateAsUpdate(newYDoc);
    return newDocumentState;
  }

  insertAiGeneratedContent(lesson: AiContentProcessor): void {
    // Create board folder
    this.getYDoc().transact(() => {
      const boardFolder = lesson.getBoardFolder();
      if (boardFolder) {
        this.addBoardFolder(boardFolder);
      }
      const room = this.getRooms()[0];
      const frames = lesson.getFrames();

      // Add frames to the room
      for (const frame of frames) {
        this.addFrame(room.uid, frame);
      }

      // Add items to the frames
      const fabricObjects = lesson.getFabricObjects();
      for (const [frameUid, objects] of fabricObjects.entries()) {
        this.addObjects(frameUid, objects, false);
      }

      // Add canvas items
      const frameItems = lesson.getFrameItems();
      for (const [frameUid, items] of frameItems.entries()) {
        this.addCanvasItems(frameUid, items, false);
      }
    });
  }
}

// createYObservable takes in any y.js type and then creates an observable from any changes made on that object
export function createYObservable<E, Y>(
  yType: Y.AbstractType<E>,
  func: (local: boolean, lastActionIsLocal: boolean) => Y,
): Observable<Y> {
  const subject: Subject<Y> = new Subject();

  // When a change is made we call this function, which runs the callers func and uses that result
  // as the next output to the subject
  const f = (ev: Array<Y.YEvent<any>>, transaction: Y.Transaction) => {
    // Undo / Redo are local events, but should be processed like remote ones
    const local =
      transaction.local && !(transaction?.origin?.undoing || transaction?.origin?.redoing);
    const lastActionIsLocal =
      transaction.local || !!transaction?.origin?.undoing || !!transaction?.origin?.redoing;
    const res = func(local, lastActionIsLocal);
    subject.next(res);
  };

  // Observe changes to the Y.Type and its children
  yType.observeDeep(f);

  const observable = subject.pipe(
    // When the observable completes or errors. In our case because share() does
    // multicasting and recounting this will be called when all subscribers unsubscribe
    finalize(() => {
      // Remove the event listener on the Y.Type
      yType.unobserveDeep(f);
    }),
    share(),
  );

  return observable;
}

/**
 * setterFn: typically this will be used to update the index at `lastIndex` to merge values with
 *           the duplicated value. If unset duplicated values will just be deleted
 */
const deduplicateUniqueYArray = <T>(
  yArray: Y.Array<T>,
  keyFn: (item: T) => string,
  mergeFn?: (yArray: Y.Array<T>, lastIndex: number, duplicatedValue: T) => void,
) => {
  try {
    const seenItems: Record<string, number> = {};
    const itemsToDelete: number[] = [];

    for (let i = yArray.length - 1; i >= 0; i--) {
      const item = yArray.get(i);
      const key = keyFn(item);

      if (key in seenItems) {
        // Mark this item for deletion
        itemsToDelete.push(i);

        // If a setter function is provided, use it to update the previously seen item
        // This assumes setterFn can handle merging or updating items as necessary
        if (mergeFn) {
          mergeFn(yArray, seenItems[key], item);
        }
      } else {
        // Store the index of the first occurrence of this item
        seenItems[key] = i;
      }
    }

    // Delete marked items in reverse order to avoid shifting indices affecting the deletion
    for (const itemToDelete of itemsToDelete) {
      yArray.delete(itemToDelete);
    }

    return itemsToDelete.length > 0;
  } catch (err) {
    console.warn('Failed to deduplicate unique yArray', err);
  }
};
